/**
 * Plugin extensions registry — generic, plugin-agnostic.
 *
 * Mirror of lesscms's contract (app/vue/src/composables/usePluginExtensions.ts)
 * adapted for lesscommerce's Vue CLI + JS stack. The host (main.js) builds
 * the registry by scanning `src/plugin-extensions/<id>/` and provides it via:
 *
 *   app.provide(PLUGIN_EXTENSIONS_KEY, registry)
 *
 * Components like `PluginSlot` call `usePluginExtensions()` / `useSlotEntries()`
 * and consume the data without knowing where extensions live on disk.
 *
 * Registry shape:
 *   {
 *     pages: [{ pluginId, route, loader }],
 *     slots: { [slotName]: [{ pluginId, key, label, loader }] }
 *   }
 *
 * `loader` is `() => Promise<Component>` — wrap with `defineAsyncComponent`
 * at consumption site for lazy loading.
 */

import { inject } from "vue";

// String literal (not Symbol) on purpose: same reasoning as lesscms — when
// modules can be loaded twice (yarn-link, dual webpack chunks, etc.) Symbol
// identity does not survive across instances. A string key always matches.
export const PLUGIN_EXTENSIONS_KEY = "lcms-plugin-extensions";

const EMPTY_REGISTRY = { pages: [], slots: {} };

export function usePluginExtensions() {
  return inject(PLUGIN_EXTENSIONS_KEY, EMPTY_REGISTRY);
}

export function useSlotEntries(slotName) {
  return usePluginExtensions().slots[slotName] || [];
}
